import type {
	ContentTypeCommentAgentReplyTarget,
	ContentTypeCommentAgentSession,
} from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';

import { ContentCommentAgentReplyService } from './content-comment-agent-reply-service.ts';

type Listener = (sessions: ContentTypeCommentAgentSession[]) => void;

export function isCommentAgentSessionScopeValid(
	contentId: string,
	cloudId: string | null | undefined,
	userId: string | null | undefined,
): boolean {
	return !!contentId && !!cloudId && !!userId && userId !== 'unidentified';
}

/**
 * Content whose host shell owns comment-session discovery. Slides share the whiteboard shell but
 * stay out.
 */
export function isCommentAgentSessionContentType(contentType: string): boolean {
	return contentType === 'whiteboard' || contentType === 'database';
}

/** Buffers the latest content snapshot for iframe listeners. Session lifecycle stays in the host. */
export class ContentCommentAgentSessionService {
	constructor(readonly scopeKey: string = '') {}
	readonly replies: ContentCommentAgentReplyService = new ContentCommentAgentReplyService();
	private listeners = new Set<Listener>();
	private demandListeners = new Set<() => void>();
	private sessions: ContentTypeCommentAgentSession[] = [];

	observe = (listener: Listener): (() => void) => {
		this.listeners.add(listener);
		this.notifyDemand();
		this.notify(listener);
		return () => {
			if (this.listeners.delete(listener)) this.notifyDemand();
		};
	};

	hasListeners = (): boolean => this.listeners.size > 0;

	subscribeToDemand = (listener: () => void): (() => void) => {
		this.demandListeners.add(listener);
		return () => {
			this.demandListeners.delete(listener);
		};
	};

	private notifyDemand(): void {
		this.demandListeners.forEach((listener) => listener());
	}

	publish = (sessions: ContentTypeCommentAgentSession[]): void => {
		this.sessions = sessions;
		this.listeners.forEach((listener) => this.notify(listener));
	};

	dispose = (): void => {
		this.replies.dispose();
		this.publish([]);
		this.listeners.clear();
		this.notifyDemand();
	};

	private notify(listener: Listener): void {
		try {
			listener(this.sessions.map((session) => ({ ...session })));
		} catch {
			// A throwing listener is broken; do not retain it or repeatedly call into a detached frame.
			this.listeners.delete(listener);
			this.notifyDemand();
		}
	}
}

export type CommentAgentSessionBridge = {
	observeCommentAgentSessions: ContentCommentAgentSessionService['observe'];
	observeCommentAgentReply: ContentCommentAgentReplyService['observe'];
	resolveCommentAgentReply: ContentCommentAgentReplyService['resolve'];
	stopCommentAgentSession: (input: ContentTypeCommentAgentReplyTarget) => Promise<boolean>;
	openCommentAgentChat: (input: ContentTypeCommentAgentReplyTarget) => void;
};
